import React, { useEffect, useState, useCallback } from "react";
import { Inbox, Send, FileText, Trash2, RefreshCw, X, Sparkles, AlertCircle, Mail, ChevronRight, Reply, Users } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const FOLDER_ICONS = {
    INBOX: Inbox,
    "Sent Items": Send,
    Drafts: FileText,
    "Deleted Items": Trash2,
};

const MailboxPanel = ({ setTab }) => {
    const [status, setStatus] = useState(null);
    const [folders, setFolders] = useState([]);
    const [folder, setFolder] = useState("INBOX");
    const [messages, setMessages] = useState([]);
    const [selected, setSelected] = useState(null);
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [composeOpen, setComposeOpen] = useState(false);
    const [replyState, setReplyState] = useState({ open: false });
    const [reasonText, setReasonText] = useState("");
    const [mailAccounts, setMailAccounts] = useState([]);
    // Which mailbox we're VIEWING (decoupled from which mailbox sends).
    // "" = the active sender; non-empty = specific account.id.
    const [viewAccountId, setViewAccountId] = useState("");

    // Params bag every IMAP call shares — folder + (optionally) account_id.
    const viewParams = useCallback(
        (extra = {}) => (viewAccountId ? { ...extra, account_id: viewAccountId } : extra),
        [viewAccountId]
    );

    const loadMailAccounts = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/mail-accounts");
            setMailAccounts(data || []);
        } catch {
            setMailAccounts([]);
        }
    }, []);

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/mail/status", { params: viewParams() });
            setStatus(data);
        } catch {
            setStatus(null);
        }
    }, [viewParams]);

    const loadFolders = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/mail/folders", { params: viewParams() });
            if (data.connected) {
                setFolders(data.folders || []);
                setReasonText("");
            } else {
                setFolders([]);
                setReasonText(data.reason || "");
            }
        } catch (e) {
            setReasonText(e?.response?.data?.detail || "Failed to load folders");
        }
    }, [viewParams]);

    const loadMessages = useCallback(async (f) => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/mail/messages", { params: viewParams({ folder: f, limit: 50 }) });
            if (data.connected) {
                setMessages(data.messages || []);
                setReasonText("");
            } else {
                setMessages([]);
                setReasonText(data.reason || "IMAP not configured");
            }
        } catch (e) {
            setReasonText(e?.response?.data?.detail || "Failed");
        } finally {
            setLoading(false);
        }
    }, [viewParams]);

    useEffect(() => {
        loadMailAccounts();
    }, [loadMailAccounts]);

    // Reload everything when the view-account changes (including initial mount with "").
    useEffect(() => {
        setSelected(null);
        setSelectedDetail(null);
        loadStatus();
        loadFolders();
        loadMessages(folder);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewAccountId]);

    const openMessage = async (uid) => {
        setSelected(uid);
        setSelectedDetail(null);
        try {
            const { data } = await api.get(`/admin/mail/messages/${uid}`, { params: viewParams({ folder }) });
            setSelectedDetail(data);
            api.post(`/admin/mail/messages/${uid}/read`, null, { params: viewParams({ folder, read: true }) }).catch(() => {});
            setMessages((m) => m.map((mm) => (mm.uid === uid ? { ...mm, seen: true } : mm)));
        } catch (e) {
            toast.error("Failed to load message");
        }
    };

    const onDelete = async (uid) => {
        try {
            await api.delete(`/admin/mail/messages/${uid}`, { params: viewParams({ folder }) });
            setMessages((m) => m.filter((mm) => mm.uid !== uid));
            if (selected === uid) {
                setSelected(null);
                setSelectedDetail(null);
            }
            toast.success("Message moved to Trash");
        } catch {
            toast.error("Delete failed");
        }
    };

    const switchFolder = (f) => {
        setFolder(f);
        setSelected(null);
        setSelectedDetail(null);
        loadMessages(f);
    };

    const connected = status?.imap_configured && status?.smtp_configured;

    return (
        <div data-testid="admin-mailbox-panel" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                    <Mail className="w-6 h-6 text-[#E53935]" strokeWidth={1.5} />
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Mailbox</h2>
                    {status && (
                        <span
                            data-testid="mailbox-status-badge"
                            className={`text-xs px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                                connected ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
                            }`}
                        >
                            {connected ? "Connected" : status.smtp_configured ? "Send only" : "Not connected"}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => loadMessages(folder)}
                        data-testid="mailbox-refresh-btn"
                        className="inline-flex items-center gap-2 text-sm font-medium text-[#1C252E] hover:text-[#E53935]"
                        aria-label="Refresh inbox"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
                        Refresh
                    </button>
                    <button
                        onClick={() => setComposeOpen(true)}
                        data-testid="mailbox-compose-btn"
                        className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5"
                    >
                        <Send className="w-4 h-4" strokeWidth={2} />
                        Compose
                    </button>
                </div>
            </div>

            {/* Per-mailbox VIEW tabs — switch which inbox you're reading
                without flipping the active sender. "Active mailbox" is the
                default tab and follows whatever's currently activated. */}
            {mailAccounts.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="mailbox-account-tabs" role="tablist" aria-label="Mailbox view">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] mr-1">View:</span>
                    <button
                        onClick={() => setViewAccountId("")}
                        data-testid="mailbox-account-tab-active"
                        role="tab"
                        aria-selected={viewAccountId === ""}
                        className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition ${viewAccountId === "" ? "bg-[#E53935] text-white font-medium" : "bg-white border border-black/10 text-[#4A5568] hover:bg-[#F4F1EA]"}`}
                    >
                        <Mail className="w-3 h-3" strokeWidth={2} /> Active mailbox
                    </button>
                    {mailAccounts.map((a) => (
                        <button
                            key={a.id}
                            onClick={() => setViewAccountId(a.id)}
                            data-testid={`mailbox-account-tab-${a.id}`}
                            role="tab"
                            aria-selected={viewAccountId === a.id}
                            title={a.smtp_user}
                            className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition ${viewAccountId === a.id ? "bg-[#E53935] text-white font-medium" : "bg-white border border-black/10 text-[#4A5568] hover:bg-[#F4F1EA]"}`}
                        >
                            <Mail className="w-3 h-3" strokeWidth={2} />
                            {a.name}
                            {a.active && <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${viewAccountId === a.id ? "bg-white/25 text-white" : "bg-green-100 text-green-800"}`}>sending</span>}
                        </button>
                    ))}
                </div>
            )}

            {!status?.imap_configured && (
                <div data-testid="mailbox-not-configured" className="bg-orange-50 border border-orange-200 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="text-sm text-[#1C252E]">
                            <p className="font-medium mb-1">Mailbox not connected</p>
                            <p className="text-[#4A5568] leading-relaxed">
                                No active mailbox profile yet. Go to <button onClick={() => setTab?.("mail-accounts")} className="font-medium text-[#E53935] hover:underline">Community → Mail accounts</button>, paste a Google App Password into the <strong>Accounts</strong> or <strong>Sales</strong> profile, click <strong>Test</strong>, then <strong>Make active</strong>. This panel will start streaming your inbox the moment a profile is active.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-12 gap-4 min-h-[640px]">
                {/* Folder sidebar */}
                <div className="col-span-12 md:col-span-3" data-testid="mailbox-folder-list">
                    <div className="bg-white border border-black/5 rounded-2xl p-2 sticky top-24">
                        {(folders.length ? folders : [{ name: "INBOX" }, { name: "Sent Items" }, { name: "Drafts" }, { name: "Deleted Items" }]).map((f) => {
                            const Icon = FOLDER_ICONS[f.name] || Inbox;
                            const active = f.name === folder;
                            return (
                                <button
                                    key={f.name}
                                    onClick={() => switchFolder(f.name)}
                                    data-testid={`folder-${f.name.replace(/\s/g, "-")}`}
                                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-left transition-colors ${
                                        active
                                            ? "bg-[#E53935] text-white"
                                            : "text-[#1C252E] hover:bg-[#F4F1EA]"
                                    }`}
                                >
                                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                                    <span className="flex-1 truncate">{f.name}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Message list */}
                <div className="col-span-12 md:col-span-4" data-testid="mailbox-message-list">
                    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden h-full">
                        <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between">
                            <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">{folder}</p>
                            <span className="text-xs text-[#4A5568]">{messages.length}</span>
                        </div>
                        {loading ? (
                            <p className="text-sm text-[#4A5568] text-center py-10">Loading…</p>
                        ) : messages.length === 0 ? (
                            <div className="text-center py-12 px-6">
                                <Inbox className="w-8 h-8 mx-auto mb-3 text-[#4A5568]" strokeWidth={1.5} />
                                <p className="text-sm text-[#4A5568]">{reasonText || "No messages"}</p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-black/5 max-h-[640px] overflow-y-auto">
                                {messages.map((m) => (
                                    <li
                                        key={m.uid}
                                        onClick={() => openMessage(m.uid)}
                                        data-testid={`message-row-${m.uid}`}
                                        className={`p-4 cursor-pointer transition-colors ${
                                            selected === m.uid ? "bg-[#F4F1EA]" : "hover:bg-[#F4F1EA]/60"
                                        } ${!m.seen ? "border-l-4 border-l-[#E53935]" : ""}`}
                                    >
                                        <div className="flex items-start justify-between gap-2 mb-1">
                                            <p className={`text-sm truncate ${!m.seen ? "font-bold text-[#1C252E]" : "text-[#4A5568]"}`}>
                                                {m.from}
                                            </p>
                                            <span className="text-xs text-[#4A5568] flex-shrink-0">
                                                {m.date_iso ? new Date(m.date_iso).toLocaleDateString() : ""}
                                            </span>
                                        </div>
                                        <p className={`text-sm truncate ${!m.seen ? "text-[#1C252E] font-medium" : "text-[#4A5568]"}`}>
                                            {m.subject}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Message detail */}
                <div className="col-span-12 md:col-span-5" data-testid="mailbox-message-detail">
                    <div className="bg-white border border-black/5 rounded-2xl p-6 h-full">
                        {!selected ? (
                            <div className="text-center py-20">
                                <ChevronRight className="w-8 h-8 mx-auto mb-3 text-[#4A5568]" strokeWidth={1.5} />
                                <p className="text-sm text-[#4A5568]">Select a message to read</p>
                            </div>
                        ) : !selectedDetail ? (
                            <p className="text-sm text-[#4A5568] text-center py-20">Loading message…</p>
                        ) : (
                            <>
                                <div className="flex items-start justify-between gap-3 mb-4 pb-4 border-b border-black/5">
                                    <div className="min-w-0">
                                        <h3 className="font-heading text-xl text-[#1C252E] font-medium leading-tight mb-2 break-words">
                                            {selectedDetail.subject || "(no subject)"}
                                        </h3>
                                        <p className="text-xs text-[#4A5568] truncate">
                                            From: <span className="text-[#1C252E]">{selectedDetail.from}</span>
                                        </p>
                                        <p className="text-xs text-[#4A5568] truncate">
                                            To: <span className="text-[#1C252E]">{selectedDetail.to}</span>
                                        </p>
                                        <p className="text-xs text-[#4A5568]">{selectedDetail.date}</p>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                            onClick={() => setReplyState({ open: true, replyAll: false, uid: selectedDetail.uid, from: selectedDetail.from, subject: selectedDetail.subject })}
                                            data-testid="message-reply-btn"
                                            title="Reply"
                                            aria-label="Reply"
                                            className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#1C252E]"
                                        >
                                            <Reply className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                        <button
                                            onClick={() => setReplyState({ open: true, replyAll: true, uid: selectedDetail.uid, from: selectedDetail.from, subject: selectedDetail.subject })}
                                            data-testid="message-reply-all-btn"
                                            title="Reply all"
                                            aria-label="Reply all"
                                            className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#1C252E]"
                                        >
                                            <Reply className="w-4 h-4 rotate-180" strokeWidth={1.5} />
                                        </button>
                                        <button
                                            onClick={() => onDelete(selectedDetail.uid)}
                                            data-testid="message-delete-btn"
                                            title="Delete"
                                            aria-label="Delete message"
                                            className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#E53935]"
                                        >
                                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </div>
                                <div className="prose prose-sm max-w-none text-[#1C252E] overflow-x-auto" style={{ wordBreak: "break-word" }}>
                                    {selectedDetail.html_body ? (
                                        <div dangerouslySetInnerHTML={{ __html: selectedDetail.html_body }} />
                                    ) : (
                                        <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{selectedDetail.text_body || ""}</pre>
                                    )}
                                </div>
                                {selectedDetail.attachments?.length > 0 && (
                                    <div className="mt-6 pt-4 border-t border-black/5">
                                        <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-2">
                                            Attachments ({selectedDetail.attachments.length})
                                        </p>
                                        <ul className="space-y-1.5">
                                            {selectedDetail.attachments.map((a, i) => (
                                                <li key={i} className="flex items-center gap-2 text-sm">
                                                    <FileText className="w-3.5 h-3.5 text-[#4A5568]" strokeWidth={1.5} />
                                                    <span className="font-medium text-[#1C252E]">{a.filename}</span>
                                                    <span className="text-xs text-[#4A5568]">({Math.round(a.size / 1024)} KB)</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {composeOpen && <ComposeModal initial={typeof composeOpen === "object" ? composeOpen : {}} onClose={() => setComposeOpen(false)} smtpReady={status?.smtp_configured} />}
            {replyState.open && (
                <ReplyModal
                    state={replyState}
                    folder={folder}
                    onClose={() => setReplyState({ open: false })}
                    onSent={() => setReplyState({ open: false })}
                />
            )}
        </div>
    );
};

const ComposeModal = ({ initial = {}, onClose, smtpReady }) => {
    const [templates, setTemplates] = useState([]);
    const [form, setForm] = useState({
        to: initial.replyTo || "",
        cc: "",
        subject: initial.subject || "",
        html: "",
    });
    const [aiOpen, setAiOpen] = useState(false);
    const [aiIntent, setAiIntent] = useState("");
    const [aiLoading, setAiLoading] = useState(false);
    const [sending, setSending] = useState(false);

    // Bulk-list mode
    const [bulkMode, setBulkMode] = useState(false);
    const [lists, setLists] = useState([]);
    const [selectedList, setSelectedList] = useState(null);
    const [listOpen, setListOpen] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);

    useEffect(() => {
        api.get("/admin/mail/templates").then((r) => setTemplates(r.data || [])).catch(() => {});
        api.get("/admin/mail/lists").then((r) => setLists(r.data || [])).catch(() => {});
    }, []);

    const applyTemplate = async (id) => {
        try {
            const { data } = await api.get(`/admin/mail/templates/${id}`);
            setForm((f) => ({ ...f, subject: data.subject || f.subject, html: data.html || "" }));
            toast.success(`Template '${data.name}' loaded`);
        } catch {
            toast.error("Failed to load template");
        }
    };

    const aiCompose = async () => {
        if (!aiIntent.trim()) {
            toast.error("Describe what you want to say");
            return;
        }
        setAiLoading(true);
        try {
            const { data } = await api.post("/admin/mail/ai-compose", {
                intent: aiIntent,
                draft: form.html || undefined,
            });
            setForm((f) => ({
                ...f,
                subject: data.subject || f.subject,
                html: data.html || f.html,
            }));
            setAiOpen(false);
            setAiIntent("");
            toast.success("AI draft ready — review before sending");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI compose failed");
        } finally {
            setAiLoading(false);
        }
    };

    const pickList = (list) => {
        setSelectedList(list);
        setBulkMode(true);
        setListOpen(false);
        toast.success(`Sending to ${list.count} recipient${list.count === 1 ? "" : "s"} — ${list.label}`);
    };

    const exitBulkMode = () => {
        setBulkMode(false);
        setSelectedList(null);
        setBulkResult(null);
    };

    const send = async () => {
        if (bulkMode) {
            if (!selectedList) return;
            if (!form.subject || !form.html) {
                toast.error("Subject and message body are required");
                return;
            }
            if (!window.confirm(`Send this email to ${selectedList.count} recipient${selectedList.count === 1 ? "" : "s"} on '${selectedList.label}'?`)) {
                return;
            }
            setSending(true);
            try {
                const { data } = await api.post("/admin/mail/send-bulk", {
                    list_id: selectedList.id,
                    subject: form.subject,
                    html_body: form.html,
                });
                setBulkResult(data);
                if (data.sent > 0) {
                    toast.success(`Sent to ${data.sent} / ${data.total}`);
                } else if (!data.smtp_configured) {
                    toast.error("SMTP not configured — every send failed");
                } else {
                    toast.error(`All ${data.total} sends failed`);
                }
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Bulk send failed");
            } finally {
                setSending(false);
            }
            return;
        }
        // Single send
        if (!form.to || !form.subject || !form.html) {
            toast.error("To, subject, and message body are required");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post("/admin/mail/send", {
                to: form.to,
                subject: form.subject,
                html_body: form.html,
                cc: form.cc ? form.cc.split(",").map((s) => s.trim()).filter(Boolean) : null,
            });
            if (data.sent) {
                toast.success("Sent");
                onClose();
            } else if (!data.connected) {
                toast.error("SMTP not configured — set SMTP_* in backend/.env");
            } else {
                toast.error(data.reason || "Send failed");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            data-testid="mail-compose-modal"
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full my-8 max-h-[92vh] flex flex-col"
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-black/5">
                    <h3 className="font-heading text-xl text-[#1C252E] font-medium">
                        {bulkMode ? "Bulk send" : "Compose"}
                    </h3>
                    <button
                        onClick={onClose}
                        data-testid="mail-compose-close"
                        aria-label="Close compose"
                        className="p-2 rounded-full hover:bg-[#F4F1EA]"
                    >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {bulkResult ? (
                        <div data-testid="bulk-result" className={`rounded-xl p-4 border ${bulkResult.sent > 0 ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                            <p className="font-medium text-[#1C252E] mb-2">
                                Bulk send complete: {bulkResult.sent} sent · {bulkResult.failed} failed · {bulkResult.total} total
                            </p>
                            <p className="text-xs text-[#4A5568]">List: <span className="font-mono">{bulkResult.list_id}</span></p>
                            <p className="text-xs text-[#4A5568]">Audit log saved with bulk_id <span className="font-mono">{bulkResult.bulk_id}</span></p>
                            {bulkResult.failures?.length > 0 && (
                                <details className="mt-3">
                                    <summary className="text-xs cursor-pointer text-[#E53935]">
                                        Show {bulkResult.failures.length} failure(s)
                                    </summary>
                                    <ul className="text-xs font-mono mt-2 space-y-0.5 text-[#4A5568]">
                                        {bulkResult.failures.map((f, i) => <li key={i}>• {f}</li>)}
                                    </ul>
                                </details>
                            )}
                            <button
                                onClick={onClose}
                                className="mt-3 text-sm font-medium text-[#1C252E] hover:text-[#E53935]"
                                data-testid="bulk-result-close"
                            >
                                Done
                            </button>
                        </div>
                    ) : (
                        <>
                            {!smtpReady && (
                                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-xs text-[#4A5568]">
                                    SMTP not configured — sending will fail until you add SMTP credentials to backend/.env.
                                </div>
                            )}

                            {bulkMode && selectedList ? (
                                <div data-testid="bulk-target-card" className="bg-[#F4F1EA] border border-black/5 rounded-xl p-4 flex items-center justify-between gap-3">
                                    <div className="flex items-start gap-3 min-w-0">
                                        <div className="w-10 h-10 rounded-full bg-[#E53935] flex items-center justify-center flex-shrink-0">
                                            <Users className="w-5 h-5 text-white" strokeWidth={1.5} />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-medium text-[#1C252E] truncate">{selectedList.label}</p>
                                            <p className="text-xs text-[#4A5568] truncate">
                                                {selectedList.count} recipient{selectedList.count === 1 ? "" : "s"} · {selectedList.description}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={exitBulkMode}
                                        data-testid="bulk-exit-btn"
                                        className="text-xs font-medium text-[#1C252E] hover:text-[#E53935] flex-shrink-0"
                                    >
                                        Change list
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <input
                                        type="email"
                                        placeholder="To"
                                        value={form.to}
                                        onChange={(e) => setForm({ ...form, to: e.target.value })}
                                        data-testid="mail-to-input"
                                        aria-label="To"
                                        className="w-full rounded-xl border border-black/10 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Cc (comma-separated, optional)"
                                        value={form.cc}
                                        onChange={(e) => setForm({ ...form, cc: e.target.value })}
                                        data-testid="mail-cc-input"
                                        aria-label="Cc"
                                        className="w-full rounded-xl border border-black/10 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                                    />
                                    <div>
                                        <button
                                            onClick={() => setListOpen(true)}
                                            data-testid="send-to-list-btn"
                                            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#E53935] hover:underline"
                                        >
                                            <Users className="w-3.5 h-3.5" strokeWidth={2} />
                                            Send to a list instead →
                                        </button>
                                    </div>
                                </>
                            )}

                            {templates.length > 0 && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Templates:</span>
                                    {templates.map((t) => (
                                        <button
                                            key={t.id}
                                            onClick={() => applyTemplate(t.id)}
                                            data-testid={`mail-template-btn-${t.id}`}
                                            className="text-xs px-3 py-1 rounded-full border border-black/10 hover:border-[#E53935] hover:text-[#E53935] transition-colors"
                                            title={t.description}
                                        >
                                            {t.name}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <input
                                type="text"
                                placeholder="Subject"
                                value={form.subject}
                                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                data-testid="mail-subject-input"
                                aria-label="Subject"
                                className="w-full rounded-xl border border-black/10 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                            />
                            <textarea
                                placeholder="Email body (HTML supported)"
                                value={form.html}
                                onChange={(e) => setForm({ ...form, html: e.target.value })}
                                rows={14}
                                data-testid="mail-body-input"
                                aria-label="Email body"
                                className="w-full rounded-xl border border-black/10 px-4 py-3 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                            />

                            {aiOpen && (
                                <div data-testid="ai-compose-box" className="bg-[#F4F1EA] rounded-xl p-4 space-y-2">
                                    <p className="text-xs uppercase tracking-wider font-bold text-[#E53935]">AI assist</p>
                                    <textarea
                                        placeholder="What should this email say? e.g. 'Thank our top 10 monthly donors and invite them to a private campus tour.'"
                                        value={aiIntent}
                                        onChange={(e) => setAiIntent(e.target.value)}
                                        rows={3}
                                        data-testid="ai-intent-input"
                                        className="w-full rounded-xl border border-black/10 px-3 py-2 text-sm bg-white"
                                    />
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={aiCompose}
                                            disabled={aiLoading}
                                            data-testid="ai-compose-submit"
                                            className="inline-flex items-center gap-1.5 bg-[#1C252E] hover:bg-black text-white rounded-full px-4 py-2 text-xs font-medium disabled:opacity-50"
                                        >
                                            <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                                            {aiLoading ? "Drafting…" : form.html ? "Improve current draft" : "Draft for me"}
                                        </button>
                                        <button onClick={() => setAiOpen(false)} className="text-xs text-[#4A5568] hover:text-[#1C252E]">
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            {form.html && (
                                <details className="bg-[#F4F1EA] rounded-xl">
                                    <summary className="px-4 py-2 cursor-pointer text-xs uppercase tracking-wider font-bold text-[#4A5568]">
                                        Preview
                                    </summary>
                                    <div
                                        data-testid="mail-preview"
                                        className="border-t border-black/5 p-4 bg-white"
                                        dangerouslySetInnerHTML={{ __html: form.html }}
                                    />
                                </details>
                            )}
                        </>
                    )}
                </div>
                {!bulkResult && (
                    <div className="px-6 py-4 border-t border-black/5 flex items-center justify-between gap-3 flex-wrap">
                        <button
                            onClick={() => setAiOpen(true)}
                            disabled={aiOpen}
                            data-testid="ai-compose-btn"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1C252E] hover:text-[#E53935] disabled:opacity-50"
                        >
                            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                            Compose with AI
                        </button>
                        <div className="flex items-center gap-2">
                            <button onClick={onClose} className="text-sm font-medium text-[#4A5568] hover:text-[#1C252E]">
                                Cancel
                            </button>
                            <button
                                onClick={send}
                                disabled={sending}
                                data-testid="mail-send-btn"
                                className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5 disabled:opacity-50"
                            >
                                <Send className="w-4 h-4" strokeWidth={2} />
                                {sending ? "Sending…" : bulkMode && selectedList ? `Send to ${selectedList.count}` : "Send"}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {listOpen && (
                <div
                    onClick={(e) => { e.stopPropagation(); setListOpen(false); }}
                    className="fixed inset-0 bg-black/30 z-[60] flex items-center justify-center p-4"
                    data-testid="list-picker-modal"
                >
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[80vh] flex flex-col">
                        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
                            <h4 className="font-heading text-lg text-[#1C252E] font-medium">Pick a list</h4>
                            <button onClick={() => setListOpen(false)} aria-label="Close" className="p-2 rounded-full hover:bg-[#F4F1EA]">
                                <X className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        </div>
                        <ul className="flex-1 overflow-y-auto divide-y divide-black/5" data-testid="list-picker-options">
                            {lists.length === 0 && (
                                <li className="px-6 py-10 text-center text-sm text-[#4A5568]">No lists available yet.</li>
                            )}
                            {lists.map((l) => (
                                <li key={l.id}>
                                    <button
                                        onClick={() => pickList(l)}
                                        disabled={l.count === 0}
                                        data-testid={`list-pick-${l.id}`}
                                        className="w-full px-6 py-4 text-left hover:bg-[#F4F1EA] transition-colors disabled:opacity-50 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <p className="font-medium text-[#1C252E] truncate">{l.label}</p>
                                                <p className="text-xs text-[#4A5568] truncate">{l.description}</p>
                                            </div>
                                            <span className={`text-xs px-2.5 py-0.5 rounded-full font-bold ${l.count > 0 ? "bg-[#F2D8D5] text-[#E53935]" : "bg-[#F4F1EA] text-[#4A5568]"}`}>
                                                {l.count}
                                            </span>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MailboxPanel;

// Reply / Reply-all modal. Sends through /api/admin/mail/reply which loads
// the original message server-side, auto-prefixes "Re:" to the subject,
// preserves CC list for reply-all, and appends a quoted block under the
// admin's typed body. The admin only needs to write the new content.
const ReplyModal = ({ state, folder, onClose, onSent }) => {
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const subjPrefix = (state.subject || "").toLowerCase().startsWith("re:") ? "" : "Re: ";
    const subj = `${subjPrefix}${state.subject || "(no subject)"}`;

    const send = async () => {
        if (!body.trim()) { toast.error("Write a reply first"); return; }
        setSending(true);
        try {
            const html = body.replace(/\n/g, "<br/>");
            const { data } = await api.post("/admin/mail/reply", {
                folder,
                uid: state.uid,
                reply_all: state.replyAll,
                html_body: html,
            });
            if (data?.sent || data?.ok) {
                toast.success(`Reply sent to ${data.to}${(data.cc || []).length ? ` (+${data.cc.length} cc)` : ""}`);
                onSent?.();
            } else {
                toast.error(data?.reason || "Send failed");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send reply");
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4 py-6"
            onClick={onClose}
            data-testid="mail-reply-modal"
        >
            <div
                className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[88vh]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-black/5">
                    <div>
                        <p className="text-xs uppercase tracking-wider font-bold text-[#E53935]">{state.replyAll ? "Reply all" : "Reply"}</p>
                        <p className="text-sm text-[#1C252E] font-medium mt-1 truncate">{subj}</p>
                        <p className="text-xs text-[#4A5568] mt-1 truncate">To: {state.from}</p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F4F1EA]">
                        <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                </div>
                <div className="p-5 flex-1 overflow-y-auto">
                    <textarea
                        autoFocus
                        rows={10}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder="Type your reply…"
                        data-testid="mail-reply-body-input"
                        className="w-full border border-black/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                    />
                    <p className="text-[11px] text-[#4A5568] mt-2 italic">The original message will be auto-quoted below your reply.</p>
                </div>
                <div className="flex justify-end gap-2 p-5 border-t border-black/5">
                    <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-medium border border-black/10 text-[#4A5568] hover:border-[#1C252E]">Cancel</button>
                    <button
                        onClick={send}
                        disabled={sending}
                        data-testid="mail-reply-send-btn"
                        className="px-4 py-2 rounded-full text-sm font-medium bg-[#E53935] hover:bg-[#D32F2F] text-white disabled:opacity-60"
                    >
                        {sending ? "Sending…" : "Send"}
                    </button>
                </div>
            </div>
        </div>
    );
};
